/**
 * WebSocket Manager Patch for Consolidated Table
 * This file contains the patches needed for WebSocket message handling
 * to work with the consolidated database table structure
 */

// Local Meeting interface for preparation data
interface Meeting {
  id?: string;
  preparation_status?: 'not_started' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  preparation_progress?: number;
  preparation_current_task?: string;
  preparation_error_message?: string;
  preparation_started_at?: string;
  preparation_completed_at?: string;
  preparation_brief?: unknown;
  preparation_insights?: unknown;
}

// Message type mappings for backward compatibility
export const MESSAGE_TYPE_MAPPINGS: Record<string, string> = {
  'PREPARATION_UPDATE': 'preparation_status_update',
  'PREPARATION_STARTED': 'preparation_started',
  'PREPARATION_COMPLETED': 'preparation_completed',
  'PREPARATION_FAILED': 'preparation_failed',
  'PREPARATION_PROGRESS': 'preparation_progress'
};

// Status value mappings
export const STATUS_MAPPINGS: Record<string, Meeting['preparation_status']> = {
  'NOT_STARTED': 'not_started',
  'IN_PROGRESS': 'in_progress',
  'COMPLETED': 'completed',
  'FAILED': 'failed',
  'CANCELLED': 'cancelled'
};

// Field name mappings (frontend to backend)
export const FIELD_MAPPINGS: Record<string, string> = {
  'preparationStatus': 'preparation_status',
  'preparationProgress': 'preparation_progress',
  'currentTask': 'preparation_current_task',
  'errorMessage': 'preparation_error_message',
  'lastUpdated': 'preparation_last_updated'
};

/**
 * Transform incoming WebSocket message to use consolidated field names
 */
export function transformIncomingMessage(message: any): any {
  const transformed = { ...message };
  
  // Map message type
  if (MESSAGE_TYPE_MAPPINGS[message.type]) {
    transformed.type = MESSAGE_TYPE_MAPPINGS[message.type];
  }
  
  // Map status values
  if (message.status && STATUS_MAPPINGS[message.status]) {
    transformed.preparation_status = STATUS_MAPPINGS[message.status];
    delete transformed.status;
  }
  
  // Map preparationStatus field
  if (message.preparationStatus && STATUS_MAPPINGS[message.preparationStatus]) {
    transformed.preparation_status = STATUS_MAPPINGS[message.preparationStatus];
    delete transformed.preparationStatus;
  }
  
  // Map progress field
  if (message.progress !== undefined) {
    transformed.preparation_progress = message.progress;
    delete transformed.progress;
  }
  
  // Map current_task field
  if (message.current_task) {
    transformed.preparation_current_task = message.current_task;
    delete transformed.current_task;
  }
  
  // Map error fields
  if (message.error_message) {
    transformed.preparation_error_message = message.error_message;
    delete transformed.error_message;
  }
  
  return transformed;
}

/**
 * Transform outgoing WebSocket message for backward compatibility
 */
export function transformOutgoingMessage(message: any): any {
  const transformed = { ...message };
  
  // Map field names back for backend compatibility if needed
  if (transformed.preparation_status) {
    // Keep both for transition period
    transformed.status = transformed.preparation_status;
  }
  
  if (transformed.preparation_progress !== undefined) {
    transformed.progress = transformed.preparation_progress;
  }
  
  if (transformed.preparation_current_task) {
    transformed.current_task = transformed.preparation_current_task;
  }
  
  return transformed;
}

/**
 * Helper to extract preparation data from WebSocket message
 */
export function extractPreparationData(message: any): Partial<Meeting> {
  const data: Partial<Meeting> = {};
  
  // Extract preparation fields
  if (message.preparation_status) {
    data.preparation_status = message.preparation_status;
  }
  
  if (message.preparation_progress !== undefined) {
    data.preparation_progress = message.preparation_progress;
  }
  
  if (message.preparation_current_task) {
    data.preparation_current_task = message.preparation_current_task;
  }
  
  if (message.preparation_error_message) {
    data.preparation_error_message = message.preparation_error_message;
  }
  
  if (message.preparation_started_at) {
    data.preparation_started_at = message.preparation_started_at;
  }
  
  if (message.preparation_completed_at) {
    data.preparation_completed_at = message.preparation_completed_at;
  }
  
  if (message.preparation_brief) {
    data.preparation_brief = message.preparation_brief;
  }
  
  if (message.preparation_insights) {
    data.preparation_insights = message.preparation_insights;
  }
  
  return data;
}

/**
 * Check if message indicates preparation completion
 */
export function isPreparationCompleteMessage(message: any): boolean {
  return (
    message.type === 'preparation_completed' ||
    message.type === 'preparation_status_update' && message.preparation_status === 'completed' ||
    message.type === 'job_completed' ||
    message.preparation_status === 'completed'
  );
}

/**
 * Check if message indicates preparation failure
 */
export function isPreparationFailedMessage(message: any): boolean {
  return (
    message.type === 'preparation_failed' ||
    message.type === 'preparation_status_update' && message.preparation_status === 'failed' ||
    message.type === 'job_failed' ||
    message.preparation_status === 'failed'
  );
}